import { isAssignableSlug } from '@synapse/clinic-slug';

/**
 * Which clinic's URL namespace this page load is in, decided once from the first
 * path segment.
 *
 * Every clinic owns a prefix — `synapse.ceylon.care/acme/patients` — and the app
 * routes inside it via React Router's `basename`. That is what lets the ~100
 * absolute paths throughout the app (`/patients`, `/lab/reports`, the nav table)
 * stay exactly as written: with `basename="/acme"`, `<Link to="/patients">`
 * renders `/acme/patients` and `useLocation().pathname` still reads `/patients`.
 *
 * Two consequences worth knowing before touching anything here:
 *
 *  1. `basename` is fixed for the router's lifetime. Any navigation that crosses
 *     it — global login into a clinic, onboarding into a brand-new clinic, a
 *     member landing on the wrong clinic's slug — must be a HARD navigation, not
 *     `navigate()`. `globalHref` / `tenantHref` exist to build those targets.
 *  2. A first segment that could be a clinic IS treated as one. Whether that
 *     clinic exists is a server question, answered later: an unknown slug lands
 *     on the tenant tree, where the public pages show "clinic not found" and the
 *     authenticated pages redirect a signed-in member to their own slug.
 */
export type TenantContext =
  | { mode: 'tenant'; slug: string; basename: string }
  | { mode: 'global'; slug: null; basename: '/' };

const GLOBAL: TenantContext = { mode: 'global', slug: null, basename: '/' };

/**
 * Exported for tests; the app uses the `TENANT` snapshot below. `isAssignableSlug`
 * is the same gate the API mints and validates slugs with, so a segment this
 * accepts is exactly a segment that could name a clinic — which is what keeps
 * `/login` and `/p/patient/<token>` reachable rather than being read as clinics.
 */
export function readTenantContext(pathname: string): TenantContext {
  const segment = pathname.split('/')[1] ?? '';
  if (!isAssignableSlug(segment)) return GLOBAL;
  return { mode: 'tenant', slug: segment, basename: `/${segment}` };
}

/**
 * Resolved once at module load, before the router mounts, because `basename` has
 * to be known at that moment. Re-reading it later would be meaningless anyway:
 * the only way the first segment changes is a full page load.
 *
 * A `let` solely so tests can place themselves in a namespace — ESM live
 * bindings mean consumers see the override. Nothing in the app reassigns it.
 */
export let TENANT: TenantContext =
  typeof window === 'undefined' ? GLOBAL : readTenantContext(window.location.pathname);

/**
 * Put the app in a given namespace for a test. Pass `null` to restore the value
 * derived from the current URL. Test-only — the real value is fixed for the life
 * of a page load.
 */
export function __setTenantForTest(context: TenantContext | null): void {
  TENANT =
    context ??
    (typeof window === 'undefined' ? GLOBAL : readTenantContext(window.location.pathname));
}

/** Convenience for tests: a tenant context for `slug`. */
export function tenantContextFor(slug: string): TenantContext {
  return { mode: 'tenant', slug, basename: `/${slug}` };
}

/** An absolute path outside any clinic — bypasses whatever basename is active. */
export function globalHref(path: string): string {
  return path.startsWith('/') ? path : `/${path}`;
}

/** An absolute path inside the named clinic. */
export function tenantHref(slug: string, path: string): string {
  const suffix = path.startsWith('/') ? path : `/${path}`;
  // `/acme/` rather than `/acme` for the clinic's home: the trailing slash keeps
  // relative asset and link resolution behaving the same as any deeper route.
  return suffix === '/' ? `/${slug}/` : `/${slug}${suffix}`;
}

/** Where the login page lives for the namespace this page load is in. */
export function loginHref(): string {
  return TENANT.mode === 'tenant' ? tenantHref(TENANT.slug, '/login') : globalHref('/login');
}

/**
 * The clinic picker: global, because it exists precisely when we do not know
 * which clinic the user means. `clinics` is in RESERVED_SLUGS, so no clinic can
 * ever claim this path.
 */
export const CLINIC_PICKER_PATH = '/clinics';

/**
 * A path to resume inside whichever clinic the user picks.
 *
 * Validated rather than trusted: it round-trips through the query string, so it
 * is attacker-supplied by the time we read it back, and it is handed to
 * `window.location`. A leading `//` or `\` would be read as a host and send the
 * user off-origin.
 */
export function safeNextPath(raw: string | null | undefined): string | null {
  if (!raw) return null;
  if (!raw.startsWith('/')) return null;
  if (raw.startsWith('//') || raw.startsWith('/\\')) return null;
  return raw;
}

export function clinicPickerHref(next?: string): string {
  const safe = safeNextPath(next);
  return safe ? `${CLINIC_PICKER_PATH}?next=${encodeURIComponent(safe)}` : CLINIC_PICKER_PATH;
}
